import React, { memo, useCallback, useEffect, useState } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";

export const ImageNode = memo(({ data, selected, id, width, height }) => {
  const { getNode } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  const nodeData = data;
  // Get position from React Flow node and update when node moves
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);
  // Default values - use React Flow's width/height if available
  const nodeWidth = width || nodeData.width || 200;
  const nodeHeight = height || nodeData.height || 150;
  const rotation = nodeData.rotation || 0;
  const borderRadius = nodeData.borderRadius || 8;
  const opacity = nodeData.opacity || 1;
  const scale = nodeData.scale || 1;

  const borderColor = nodeData.borderColor || "#000000";
  const borderWidth = nodeData.borderWidth || 0;
  const borderStyle = nodeData.borderStyle || "solid";
  const lineCap = nodeData.lineCap || "butt";
  const lineJoin = nodeData.lineJoin || "miter";

  const getDashArray = (style) => {
    switch (style) {
      case "dashed":
        return "10,5";
      case "dotted":
        return "2,3";
      default:
        return "none";
    }
  };

  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "image",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id],
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "image",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  // Check if node is locked
  const isLocked = nodeData.locked || false;

  return (
    <div className="relative w-full h-full" data-id={id}>
      {/* NodeResizer/SelectionOverlay must be direct child of node for React Flow to work properly */}
      {!isLocked && selected && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={50}
          minHeight={50}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}
      <div
        className="relative hover:shadow-lg w-full h-full"
        style={{
          transform: `rotate(${rotation}deg) scale(${scale}) scale(${nodeData.flippedH ? -1 : 1}, ${nodeData.flippedV ? -1 : 1})`,
          opacity: opacity,
          borderRadius: `${borderRadius}px`,
          overflow: "hidden",
          transition: "box-shadow 0.2s ease, opacity 0.2s ease", // Only transition shadow and opacity, NOT transform
        }}
      >
        <img
          src={nodeData.src}
          alt={nodeData.alt || "Floor plan image"}
          className="w-full h-full object-cover"
          style={{
            borderRadius: `${borderRadius}px`,
          }}
          onError={(e) => {
            // Fallback for broken images
            const target = e.target;
            target.src =
              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDIwMCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTUwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik02MCA3NUgxNDBNNzUgNjBWOTAiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+";
          }}
        />

        {/* SVG Stroke Overlay */}
        {borderWidth > 0 && borderStyle !== "none" && (
          <svg
            className="absolute inset-0 pointer-events-none"
            width="100%"
            height="100%"
            viewBox={`0 0 ${nodeWidth} ${nodeHeight}`}
            preserveAspectRatio="none"
            style={{
              overflow: "visible",
            }}
          >
            <rect
              x={borderWidth / 2}
              y={borderWidth / 2}
              width={Math.max(nodeWidth - borderWidth, 0)}
              height={Math.max(nodeHeight - borderWidth, 0)}
              rx={borderRadius}
              ry={borderRadius}
              fill="none"
              stroke={borderColor}
              strokeWidth={borderWidth}
              strokeDasharray={getDashArray(borderStyle)}
              strokeLinecap={lineCap}
              strokeLinejoin={lineJoin}
            />
          </svg>
        )}

        {/* Handles */}
        <Handle
          type="target"
          position={Position.Top}
          className="w-3 h-3 bg-primary border-2 border-white"
        />

        <Handle
          type="source"
          position={Position.Bottom}
          className="w-3 h-3 bg-primary border-2 border-white"
        />

        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-primary border-2 border-white"
        />

        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-primary border-2 border-white"
        />
      </div>
    </div>
  );
});
